import { useState } from 'react'
import { Plus, ShieldAlert, X } from 'lucide-react'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Typography } from '@/components/ui/typography'
import { OgsmAccessField } from '@/components/custom/admin/ogsm-access-field'
import { UserAppAccessFields } from '@/components/custom/admin/user-app-access-fields'
import {
  UserSheet,
  UserSheetBody,
  UserSheetFieldLabel,
  UserSheetFooter,
} from '@/components/custom/admin/user-sheet'
import {
  type AdminUser,
  useChangeInternalUserOgsmAccess,
  useChangeUserRole,
  useDeleteCompanyAssignment,
  useUpdateUserAccess,
} from '@/hooks/use-admin'
import {
  sameAllowedApps,
  saveErrorMessage,
} from '@/features/admin/user-sheet-save'
import { OGSM_ACCESS_COPY } from '@/features/ogsm/access'
import { selectUser, useUserStore } from '@/stores/user.store'
import { showError, showSuccess } from '@/lib/toast'
import type { OgsmAccess } from '@/services/portal.service'
import type { AllowedApp } from '@/types/api'

const INTERNAL_ROLES = [
  { value: 'GREYBULL_MEMBER', label: 'Member' },
  { value: 'HEAD_OF_PORTFOLIO', label: 'Head of Portfolio' },
  { value: 'GREYBULL_ADMIN', label: 'Admin' },
]

const ASSIGNMENT_FULL: Record<string, string> = {
  PRIMARY_COVERAGE: 'Primary Owner',
  SECONDARY_COVERAGE: 'Secondary Owner',
}

function getInternalRoleLabel(role: string) {
  return INTERNAL_ROLES.find((r) => r.value === role)?.label ?? role
}

interface EditGreybullUserSheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  user: AdminUser
  onAddCompanyClick: () => void
  /** Shows the OGSM access field (VITE_OGSM_ENABLED); hidden anyway when the user has no stored level. */
  showOgsmAccess?: boolean
}

export function EditGreybullUserSheet({
  open,
  onOpenChange,
  user,
  onAddCompanyClick,
  showOgsmAccess = false,
}: EditGreybullUserSheetProps) {
  const currentProfile = useUserStore(selectUser)
  const isCurrentUser = currentProfile?.id === user.id

  const changeRole = useChangeUserRole()
  const updateAccess = useUpdateUserAccess()
  const changeOgsmAccessLevel = useChangeInternalUserOgsmAccess()
  const deleteAssignment = useDeleteCompanyAssignment()

  const storedOgsmAccess = user.ogsmAccess ?? null
  const showOgsm = showOgsmAccess && storedOgsmAccess !== null

  const [role, setRole] = useState(user.role)
  const [allowedApps, setAllowedApps] = useState<AllowedApp[]>(user.allowedApps)
  const [ogsmAccess, setOgsmAccess] = useState<OgsmAccess>(
    storedOgsmAccess ?? 'EDIT'
  )
  const [roleError, setRoleError] = useState<string | null>(null)
  const [appsError, setAppsError] = useState<string | null>(null)
  const [ogsmError, setOgsmError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [pendingDemoteRole, setPendingDemoteRole] = useState<string | null>(
    null
  )

  const isBusy = saving || deleteAssignment.isPending
  const roleChanged = role !== user.role
  const appsChanged = !sameAllowedApps(user.allowedApps, allowedApps)
  // Admins always edit OGSM (backend rule); nobody changes their own level.
  const ogsmLocked = isCurrentUser || role === 'GREYBULL_ADMIN'
  const ogsmChanged = showOgsm && !ogsmLocked && ogsmAccess !== storedOgsmAccess
  const isDirty = roleChanged || appsChanged || ogsmChanged

  const isSelfDemote =
    isCurrentUser && user.role === 'GREYBULL_ADMIN' && role !== 'GREYBULL_ADMIN'

  const changeRoleDraft = (next: string) => {
    setRole(next)
    setRoleError(null)
  }

  const changeAllowedApps = (next: AllowedApp[]) => {
    setAllowedApps(next)
    setAppsError(null)
  }

  const changeOgsmAccess = (next: OgsmAccess) => {
    setOgsmAccess(next)
    setOgsmError(null)
  }

  const applyChanges = async () => {
    setSaving(true)
    setRoleError(null)
    setAppsError(null)
    setOgsmError(null)
    let roleSaved = false
    let appsSaved = false
    try {
      if (roleChanged) {
        try {
          await changeRole.mutateAsync({ userId: user.id, role })
          roleSaved = true
        } catch (error) {
          setRoleError(
            saveErrorMessage(error, "Couldn't save the role. Try again.")
          )
          return
        }
      }
      if (appsChanged) {
        try {
          await updateAccess.mutateAsync({ userId: user.id, allowedApps })
          appsSaved = true
        } catch (error) {
          setAppsError(
            saveErrorMessage(
              error,
              roleSaved
                ? 'Role saved, but app access did not. Try again.'
                : "Couldn't save app access. Try again."
            )
          )
          return
        }
      }
      if (ogsmChanged) {
        try {
          await changeOgsmAccessLevel.mutateAsync({
            userId: user.id,
            ogsmAccess,
          })
        } catch (error) {
          setOgsmError(
            saveErrorMessage(
              error,
              roleSaved
                ? 'Role saved, but OGSM access did not. Try again.'
                : appsSaved
                  ? 'App access saved, but OGSM access did not. Try again.'
                  : "Couldn't save OGSM access. Try again."
            )
          )
          return
        }
      }
      showSuccess('Changes saved')
      onOpenChange(false)
    } finally {
      setSaving(false)
    }
  }

  const handleSave = () => {
    if (!isDirty || isBusy || allowedApps.length === 0) return
    if (isSelfDemote) {
      setPendingDemoteRole(role)
      return
    }
    void applyChanges()
  }

  const handleRemoveAssignment = async (
    companyId: string,
    companyName: string
  ) => {
    try {
      await deleteAssignment.mutateAsync({ userId: user.id, companyId })
      showSuccess(`${companyName} removed`)
    } catch (error) {
      showError(error, 'Failed to remove assignment')
    }
  }

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      setRole(user.role)
      setAllowedApps(user.allowedApps)
      setOgsmAccess(storedOgsmAccess ?? 'EDIT')
      setRoleError(null)
      setAppsError(null)
      setOgsmError(null)
    }
    onOpenChange(isOpen)
  }

  return (
    <>
      <UserSheet
        open={open}
        onOpenChange={handleOpenChange}
        title={
          <>
            {user.name}
            {isCurrentUser && (
              <span className="ml-2 align-[0.12em] font-sans text-[15px] font-normal text-primary/70">
                (you)
              </span>
            )}
          </>
        }
        subtitle={user.email}
      >
        <UserSheetBody>
          <div>
            <UserSheetFieldLabel htmlFor="edit-greybull-user-role">
              Role
            </UserSheetFieldLabel>
            <Select
              value={role}
              onValueChange={changeRoleDraft}
              disabled={isBusy}
            >
              <SelectTrigger
                id="edit-greybull-user-role"
                className="h-10 border-primary/20"
                aria-describedby={
                  roleError ? 'edit-greybull-user-role-error' : undefined
                }
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {INTERNAL_ROLES.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {roleError && (
              <Typography
                id="edit-greybull-user-role-error"
                role="alert"
                variant="body-sm"
                className="mt-1.5 text-destructive"
              >
                {roleError}
              </Typography>
            )}
          </div>

          {showOgsm && (
            <OgsmAccessField
              id="edit-greybull-user-ogsm-access"
              value={ogsmAccess}
              onChange={changeOgsmAccess}
              locked={role === 'GREYBULL_ADMIN'}
              lockReason={
                isCurrentUser ? OGSM_ACCESS_COPY.selfHelper : undefined
              }
              disabled={isBusy}
              error={ogsmError}
            />
          )}

          <div>
            <UserAppAccessFields
              allowedApps={allowedApps}
              onAllowedAppsChange={changeAllowedApps}
              initialAllowedApps={user.allowedApps}
              context="edit"
              includeDealReview
            />
            {appsError && (
              <Typography
                role="alert"
                variant="body-sm"
                className="mt-1.5 text-destructive"
              >
                {appsError}
              </Typography>
            )}
          </div>

          <div>
            <UserSheetFieldLabel>Companies</UserSheetFieldLabel>
            <Typography variant="body-sm" className="mb-3 text-primary/70">
              Which portfolio companies this user can see in Compass.
            </Typography>

            {user.companyAssignments.length > 0 ? (
              <div className="mb-2 flex flex-col gap-1.5">
                {user.companyAssignments.map((a) => (
                  <div
                    key={a.id}
                    className="group flex items-center justify-between rounded-lg bg-primary/[0.03] px-3.5 py-2.5"
                  >
                    <div className="flex min-w-0 items-center gap-2">
                      <Typography
                        variant="body-sm"
                        as="span"
                        className="truncate text-[13px] text-primary"
                      >
                        {a.companyName}
                      </Typography>
                      <Typography
                        variant="body-sm"
                        as="span"
                        className="shrink-0 text-[12px] text-primary/70"
                      >
                        ·{' '}
                        {ASSIGNMENT_FULL[a.assignmentType] ?? a.assignmentType}
                      </Typography>
                    </div>
                    <button
                      type="button"
                      onClick={() =>
                        void handleRemoveAssignment(a.companyId, a.companyName)
                      }
                      disabled={deleteAssignment.isPending}
                      aria-label={`Remove ${a.companyName}`}
                      className="ml-2 shrink-0 cursor-pointer rounded p-1 text-primary/70 transition-colors hover:bg-destructive/5 hover:text-destructive disabled:opacity-40"
                    >
                      <X className="size-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <Typography variant="body-sm" className="mb-2 text-primary/70">
                No companies assigned.
              </Typography>
            )}

            <button
              type="button"
              onClick={onAddCompanyClick}
              className="flex w-full cursor-pointer items-center justify-center gap-2 rounded-lg border border-dashed border-primary/20 px-3 py-2.5 text-[13px] text-primary/70 transition-colors hover:border-secondary hover:bg-secondary/[0.04] hover:text-secondary"
            >
              <Plus className="size-3.5" />
              Add company
            </button>
          </div>
        </UserSheetBody>

        <UserSheetFooter>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleOpenChange(false)}
            disabled={isBusy}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={handleSave}
            disabled={!isDirty || isBusy || allowedApps.length === 0}
          >
            {isBusy ? 'Saving…' : 'Save changes'}
          </Button>
        </UserSheetFooter>
      </UserSheet>

      <AlertDialog
        open={pendingDemoteRole !== null}
        onOpenChange={(isOpen) => {
          if (!isOpen) setPendingDemoteRole(null)
        }}
      >
        <AlertDialogContent className="max-w-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <div className="flex size-6 shrink-0 items-center justify-center rounded-full bg-destructive/10">
                <ShieldAlert className="size-3 text-destructive" />
              </div>
              Remove your admin access?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Changing your role to{' '}
              <span className="font-medium text-primary">
                {pendingDemoteRole
                  ? getInternalRoleLabel(pendingDemoteRole)
                  : ''}
              </span>{' '}
              means you&apos;ll lose access to this page. Another admin will
              need to restore it.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="h-8 text-xs">
              Keep Admin
            </AlertDialogCancel>
            <AlertDialogAction
              className="h-8 bg-destructive text-xs text-white hover:bg-destructive/90"
              onClick={() => {
                setPendingDemoteRole(null)
                void applyChanges()
              }}
            >
              Remove access
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
